/**
 * AdminHome — private JCC Admin landing page (v1).
 *
 * Route protection: reuses the existing legacy JWT ProtectedRoute
 * (requireAdmin) wired in App.js — no new auth mechanism introduced.
 * Not linked from any public or member navigation; reachable only via
 * a direct URL known to admin.
 *
 * For this first version there is exactly one active admin section
 * (Care Needs). The grid below is deliberately built to host more
 * cards later without any restructuring.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ClipboardList, ArrowRight, UserCircle2, RefreshCw, Newspaper, FileText, Mail, ArrowRightLeft, ShieldQuestion, ShoppingBag } from "lucide-react";
import { api } from "../../lib/api";
import { PageHero } from "../../components/shared/Primitives";

export default function AdminHome() {
  const [needsAction, setNeedsAction] = useState(null); // null = still loading
  const [submittedProfiles, setSubmittedProfiles] = useState(null); // null = still loading
  const [pendingSyncs, setPendingSyncs] = useState(null); // null = still loading
  const [draftArticles, setDraftArticles] = useState(null); // null = still loading
  const [draftForms, setDraftForms] = useState(null); // null = still loading
  const [unreadContact, setUnreadContact] = useState(null); // null = still loading
  const [pendingCarerReview, setPendingCarerReview] = useState(null); // null = still loading

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get("/api/admin/carer-review/pending");
        if (alive) setPendingCarerReview((data.members || []).length);
      } catch {
        if (alive) setPendingCarerReview(null);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get("/api/admin/care-needs");
        const count = (data.posts || []).filter((p) => p.needs_action).length;
        if (alive) setNeedsAction(count);
      } catch {
        if (alive) setNeedsAction(null);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get("/api/admin/pro-carer-profiles");
        if (alive) setSubmittedProfiles((data.profiles || []).length);
      } catch {
        if (alive) setSubmittedProfiles(null);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get("/api/membership/admin/systeme-sync-status");
        const count = (data.subscriptions || []).filter((s) => s.systeme_sync_status === "pending").length;
        if (alive) setPendingSyncs(count);
      } catch {
        if (alive) setPendingSyncs(null);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get("/api/admin/articles");
        const count = (data.articles || []).filter((a) => a.status === "draft").length;
        if (alive) setDraftArticles(count);
      } catch {
        if (alive) setDraftArticles(null);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get("/api/admin/forms");
        const count = (data.templates || []).filter((t) => t.status === "draft").length;
        if (alive) setDraftForms(count);
      } catch {
        if (alive) setDraftForms(null);
      }
    })();
    (async () => {
      try {
        const { data } = await api.get("/api/admin/contact");
        if (alive) setUnreadContact(data.unread_count ?? 0);
      } catch {
        if (alive) setUnreadContact(null);
      }
    })();
    return () => { alive = false; };
  }, []);

  return (
    <div data-testid="admin-home-page">
      <PageHero eyebrow="JCC Admin" title="Admin home"
                description="Private administration area. This will grow to cover more of JCC's admin workflow over time — for now, Care Needs is the active section." />
      <section className="section pt-8">
        <div className="container-jcc max-w-5xl">
          {/* Grid designed to hold more admin section cards later —
              only one is built for this first version. */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            <Link
              to="/admin/care-needs"
              className="group card-base p-6 flex flex-col hover:border-teal hover:-translate-y-0.5 transition-all duration-200"
              data-testid="admin-care-needs-card"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-teal/10 text-teal flex items-center justify-center">
                  <ClipboardList className="w-5 h-5" strokeWidth={1.75} />
                </div>
                {needsAction !== null && needsAction > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-amber-500 text-white text-xs font-bold"
                    data-testid="admin-care-needs-count"
                  >
                    {needsAction}
                  </span>
                )}
              </div>
              <h3 className="mt-4 font-display font-semibold text-lg">Care Needs</h3>
              {needsAction !== null && (
                <p className="mt-1 text-xs font-medium text-amber-700" data-testid="admin-care-needs-count-label">
                  {needsAction > 0
                    ? `${needsAction} submission${needsAction === 1 ? "" : "s"} need${needsAction === 1 ? "s" : ""} attention`
                    : "Nothing needs attention right now"}
                </p>
              )}
              <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">
                Review and manage family Care Need submissions.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-teal font-semibold text-sm">
                Manage Care Needs
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>

            <Link
              to="/admin/pro-carer-profiles"
              className="group card-base p-6 flex flex-col hover:border-purple hover:-translate-y-0.5 transition-all duration-200"
              data-testid="admin-pro-carer-profiles-card"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-purple/10 text-purple flex items-center justify-center">
                  <UserCircle2 className="w-5 h-5" strokeWidth={1.75} />
                </div>
                {submittedProfiles !== null && submittedProfiles > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-amber-500 text-white text-xs font-bold"
                    data-testid="admin-pro-carer-profiles-count"
                  >
                    {submittedProfiles}
                  </span>
                )}
              </div>
              <h3 className="mt-4 font-display font-semibold text-lg">Pro Carer Profiles</h3>
              {submittedProfiles !== null && (
                <p className="mt-1 text-xs font-medium text-amber-700" data-testid="admin-pro-carer-profiles-count-label">
                  {submittedProfiles > 0
                    ? `${submittedProfiles} profile${submittedProfiles === 1 ? "" : "s"} submitted for review`
                    : "Nothing submitted right now"}
                </p>
              )}
              <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">
                View submitted Pro Carer Profiles.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-purple font-semibold text-sm">
                View profiles
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>

            <Link
              to="/admin/membership-sync"
              className="group card-base p-6 flex flex-col hover:border-amber-500 hover:-translate-y-0.5 transition-all duration-200"
              data-testid="admin-membership-sync-card"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center">
                  <RefreshCw className="w-5 h-5" strokeWidth={1.75} />
                </div>
                {pendingSyncs !== null && pendingSyncs > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-amber-500 text-white text-xs font-bold"
                    data-testid="admin-membership-sync-count"
                  >
                    {pendingSyncs}
                  </span>
                )}
              </div>
              <h3 className="mt-4 font-display font-semibold text-lg">Membership Sync</h3>
              {pendingSyncs !== null && (
                <p className="mt-1 text-xs font-medium text-amber-700" data-testid="admin-membership-sync-count-label">
                  {pendingSyncs > 0
                    ? `${pendingSyncs} membership${pendingSyncs === 1 ? "" : "s"} not yet synced to Systeme`
                    : "Everything is synced"}
                </p>
              )}
              <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">
                Native Stripe memberships and their Systeme contact/tag sync health.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-amber-600 font-semibold text-sm">
                View sync status
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>

            <Link
              to="/admin/carer-review"
              className="group card-base p-6 flex flex-col hover:border-amber-500 hover:-translate-y-0.5 transition-all duration-200"
              data-testid="admin-carer-review-card"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-amber-500/10 text-amber-600 flex items-center justify-center">
                  <ShieldQuestion className="w-5 h-5" strokeWidth={1.75} />
                </div>
                {pendingCarerReview !== null && pendingCarerReview > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-amber-500 text-white text-xs font-bold"
                    data-testid="admin-carer-review-count"
                  >
                    {pendingCarerReview}
                  </span>
                )}
              </div>
              <h3 className="mt-4 font-display font-semibold text-lg">Carer Identity Review</h3>
              {pendingCarerReview !== null && (
                <p className="mt-1 text-xs font-medium text-amber-700" data-testid="admin-carer-review-count-label">
                  {pendingCarerReview > 0
                    ? `${pendingCarerReview} carer account${pendingCarerReview === 1 ? "" : "s"} awaiting review`
                    : "Nothing awaiting review"}
                </p>
              )}
              <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">
                Flagged carer registrations (organisational-account indicators) plus a read-only report of existing accounts.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-amber-600 font-semibold text-sm">
                Review accounts
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>

            <Link
              to="/admin/articles"
              className="group card-base p-6 flex flex-col hover:border-teal hover:-translate-y-0.5 transition-all duration-200"
              data-testid="admin-articles-card"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-teal/10 text-teal flex items-center justify-center">
                  <Newspaper className="w-5 h-5" strokeWidth={1.75} />
                </div>
                {draftArticles !== null && draftArticles > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-slate-400 text-white text-xs font-bold"
                    data-testid="admin-articles-count"
                  >
                    {draftArticles}
                  </span>
                )}
              </div>
              <h3 className="mt-4 font-display font-semibold text-lg">Articles</h3>
              {draftArticles !== null && (
                <p className="mt-1 text-xs font-medium text-ink-muted" data-testid="admin-articles-count-label">
                  {draftArticles > 0
                    ? `${draftArticles} draft${draftArticles === 1 ? "" : "s"} not yet published`
                    : "No drafts waiting"}
                </p>
              )}
              <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">
                Create and manage WIM Blog and Care News articles.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-teal font-semibold text-sm">
                Manage articles
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>

            <Link
              to="/admin/shop"
              className="group card-base p-6 flex flex-col hover:border-teal hover:-translate-y-0.5 transition-all duration-200"
              data-testid="admin-shop-card"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-teal/10 text-teal flex items-center justify-center">
                  <ShoppingBag className="w-5 h-5" strokeWidth={1.75} />
                </div>
              </div>
              <h3 className="mt-4 font-display font-semibold text-lg">JCC Shop</h3>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">
                Manage downloadable products, preview images, the customer PDF and orders.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-teal font-semibold text-sm">
                Manage Shop
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>

            <Link
              to="/admin/forms"
              className="group card-base p-6 flex flex-col hover:border-purple hover:-translate-y-0.5 transition-all duration-200"
              data-testid="admin-forms-card"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-purple/10 text-purple flex items-center justify-center">
                  <FileText className="w-5 h-5" strokeWidth={1.75} />
                </div>
                {draftForms !== null && draftForms > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-slate-400 text-white text-xs font-bold"
                    data-testid="admin-forms-count"
                  >
                    {draftForms}
                  </span>
                )}
              </div>
              <h3 className="mt-4 font-display font-semibold text-lg">Form Builder</h3>
              {draftForms !== null && (
                <p className="mt-1 text-xs font-medium text-ink-muted" data-testid="admin-forms-count-label">
                  {draftForms > 0
                    ? `${draftForms} draft${draftForms === 1 ? "" : "s"} not yet published`
                    : "No drafts waiting"}
                </p>
              )}
              <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">
                Create and publish interactive, printable JCC forms — no coding or Google Drive needed.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-purple font-semibold text-sm">
                Manage forms
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>

            <Link
              to="/admin/contact"
              className="group card-base p-6 flex flex-col hover:border-teal hover:-translate-y-0.5 transition-all duration-200"
              data-testid="admin-contact-card"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-teal/10 text-teal flex items-center justify-center">
                  <Mail className="w-5 h-5" strokeWidth={1.75} />
                </div>
                {unreadContact !== null && unreadContact > 0 && (
                  <span
                    className="inline-flex items-center justify-center min-w-[1.75rem] h-7 px-2 rounded-full bg-amber-500 text-white text-xs font-bold"
                    data-testid="admin-contact-count"
                  >
                    {unreadContact}
                  </span>
                )}
              </div>
              <h3 className="mt-4 font-display font-semibold text-lg">Contact Submissions</h3>
              {unreadContact !== null && (
                <p className="mt-1 text-xs font-medium text-amber-700" data-testid="admin-contact-count-label">
                  {unreadContact > 0
                    ? `${unreadContact} unread message${unreadContact === 1 ? "" : "s"}`
                    : "All caught up"}
                </p>
              )}
              <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">
                Messages sent through the public Contact form.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-teal font-semibold text-sm">
                View submissions
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>

            <Link
              to="/admin/carer-essentials-migration"
              className="group card-base p-6 flex flex-col border-dashed hover:border-purple hover:-translate-y-0.5 transition-all duration-200"
              data-testid="admin-carer-essentials-migration-card"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-purple/10 text-purple flex items-center justify-center">
                  <ArrowRightLeft className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-soft">Temporary</span>
              </div>
              <h3 className="mt-4 font-display font-semibold text-lg">Carer Essentials Migration</h3>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">
                One-off tool to move legacy £7 subscribers onto the carer_essentials entitlement.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-purple font-semibold text-sm">
                Open migration tool
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>

            <Link
              to="/admin/forms-preview-migration"
              className="group card-base p-6 flex flex-col border-dashed hover:border-purple hover:-translate-y-0.5 transition-all duration-200"
              data-testid="admin-forms-preview-migration-card"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-purple/10 text-purple flex items-center justify-center">
                  <ArrowRightLeft className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-soft">Temporary</span>
              </div>
              <h3 className="mt-4 font-display font-semibold text-lg">Forms: Preview → Production</h3>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">
                One-off tool to copy Forms Library templates built on Preview into Production.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-purple font-semibold text-sm">
                Open migration tool
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>

            <Link
              to="/admin/forms-update-migration"
              className="group card-base p-6 flex flex-col border-dashed hover:border-purple hover:-translate-y-0.5 transition-all duration-200"
              data-testid="admin-forms-update-migration-card"
            >
              <div className="flex items-center justify-between">
                <div className="w-10 h-10 rounded-xl bg-purple/10 text-purple flex items-center justify-center">
                  <ArrowRightLeft className="w-5 h-5" strokeWidth={1.75} />
                </div>
                <span className="text-[10px] font-semibold uppercase tracking-wide text-ink-soft">Temporary</span>
              </div>
              <h3 className="mt-4 font-display font-semibold text-lg">Forms: Update Migrated</h3>
              <p className="mt-2 text-sm text-ink-muted leading-relaxed flex-1">
                One-off tool to sync the 26 already-migrated forms with their latest Preview edits.
              </p>
              <span className="mt-4 inline-flex items-center gap-1.5 text-purple font-semibold text-sm">
                Open migration tool
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
